// src/pages/PassLogin.js
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import StageFrame from '../components/StageFrame';
import FullBleed from '../components/FullBleed';
import bg from '../assets/card.png';
import cardApply from '../assets/card_apply.png';
import { formatPhoneInput } from '../utils/phone';
import PhoneInput from '../components/PhoneInput';

axios.defaults.withCredentials = true;

const TAB_POS = { x: 70, y: 250, w: 940 };
const digits = (s) => formatPhoneInput(s);

export default function PassLogin(){
  const [step, setStep] = useState('phone'); // 'phone' | 'pin' | 'setpin' | 'register'
  const [busy, setBusy] = useState(false);
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [pin2, setPin2] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [birthday, setBirthday] = useState(''); // YYYY-MM-DD
  const [msg, setMsg] = useState('');

  const phoneRef = useRef(null);
  const pinRef   = useRef(null);
  const emailRef = useRef(null);

  const getReturnUrl = () => {
    const usp = new URLSearchParams(window.location.search);
    const from = usp.get('from');
    if (from) {
      try {
        // 解碼 from 參數（可能是完整的 URL）
        const decoded = decodeURIComponent(from);
        // 如果是完整 URL，只取 pathname + search
        if (decoded.startsWith('http')) {
          const url = new URL(decoded);
          return url.pathname + url.search;
        }
        return decoded;
      } catch (e) {
        console.warn('Failed to decode from param:', e);
        return from;
      }
    }
    return '/card';
  };

  // === 自動 focus ===
  useEffect(() => {
    const t = setTimeout(() => {
      if (step === 'phone') phoneRef.current?.focus?.();
      if (step === 'pin' || step === 'setpin') pinRef.current?.focus?.();
      if (step === 'register') emailRef.current?.focus?.();
    }, 50);
    return () => clearTimeout(t);
  }, [step]);

  // === NEW: 讀取上一個電話，若存在則自動檢查該電話的狀態（省一步） ===
  useEffect(() => {
    const last = localStorage.getItem('pass_phone') || '';
    if (!last) return;
    setPhone(last);
    (async () => {
      try {
        setBusy(true);
        setMsg('');
        const { data } = await axios.post('/auth/pass/check', { phone: digits(last) });
        setBusy(false);
        if (!data?.ok) return; // 若檢查失敗就維持停在電話頁
        if (data.hasPass) {
          setStep('pin');
        } else if (data.hasMember) {
          setName(data.member?.name || '');
          setEmail(data.member?.email || '');
          setBirthday(data.member?.birthday || '');
          setStep('setpin');
        } else {
          setStep('register');
        }
      } catch {
        setBusy(false);
      }
    })();
  }, []);

  const onSubmitPhone = async (e) => {
    e.preventDefault();
    setMsg('');
    // 移除格式限制：接受任何格式的電話號碼
    const p = String(phone || '').trim();
    if (!p) return setMsg('請輸入電話');
    try {
      setBusy(true);
      // NEW: 記住電話
      localStorage.setItem('pass_phone', p);
      const { data } = await axios.post('/auth/pass/check', { phone: p });
      setBusy(false);
      if (!data?.ok) return setMsg('檢查失敗，請稍後再試');
      if (data.hasPass) {
        setStep('pin'); // 已有 PASS → 要 PIN 登入
      } else if (data.hasMember) {
        // 已有會員未設 PIN：帶出基本資料（生日可改）
        setName(data.member?.name || '');
        setEmail(data.member?.email || '');
        setBirthday(data.member?.birthday || ''); // 'YYYY-MM-DD' 或空
        setStep('setpin');
      } else {
        setStep('register'); // 完全新用戶
      }
    } catch {
      setBusy(false);
      setMsg('檢查失敗，請稍後再試');
    }
  };

  const onSubmitPinLogin = async (e) => {
    e.preventDefault();
    setMsg('');
    // 移除格式限制：接受任何格式的電話號碼
    const p = String(phone || '').trim();
    if (!p) return setMsg('請輸入電話');
    if (!/^\d{4,8}$/.test(pin)) return setMsg('PIN 碼需為 4~8 位數字');
    try {
      setBusy(true);
      // NEW: 仍然記住電話（成功或失敗都記，體驗較好）
      localStorage.setItem('pass_phone', p);
      const { data } = await axios.post('/auth/pass/login', { phone: p, pin });
      setBusy(false);
      if (data?.ok) {
        window.location.replace(getReturnUrl());
      } else {
        setMsg('登入失敗，請確認 PIN');
      }
    } catch {
      setBusy(false);
      setMsg('登入失敗，請確認 PIN');
    }
  };

  const onSubmitSetPin = async (e) => {
    e.preventDefault();
    setMsg('');
    // 移除格式限制：接受任何格式的電話號碼
    const p = String(phone || '').trim();
    if (!p) return setMsg('請輸入電話');
    if (!/^\d{4,8}$/.test(pin)) return setMsg('PIN 碼需為 4~8 位數字');
    if (pin !== pin2) return setMsg('兩次 PIN 碼不一致');
    try {
      setBusy(true);
      const payload = { phone: p, pin, birthday: birthday || undefined };
      // NEW: 記住電話
      localStorage.setItem('pass_phone', p);
      const { data } = await axios.post('/auth/pass/bootstrap', payload);
      setBusy(false);
      if (data?.ok) window.location.replace(getReturnUrl()); else setMsg('設定失敗，請稍後再試');
    } catch (e2) {
      setBusy(false);
      if (e2?.response?.status === 409) setMsg('此電話已設定過 PIN，請改用登入');
      else if (e2?.response?.status === 404) setMsg('查無會員資料，請改用註冊');
      else setMsg('設定失敗，請稍後再試');
    }
  };

  const onSubmitRegister = async (e) => {
    e.preventDefault();
    setMsg('');
    // 移除格式限制：接受任何格式的電話號碼（包括外國號碼如 821067808260）
    const p = String(phone || '').trim();
    if (!p) return setMsg('請輸入電話');
    if (!name.trim()) return setMsg('請輸入姓名');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setMsg('Email 格式不正確');
    if (!/^\d{4,8}$/.test(pin)) return setMsg('PIN 碼需為 4~8 位數字');
    if (pin !== pin2) return setMsg('兩次 PIN 碼不一致');
    try {
      setBusy(true);
      const payload = { phone: p, name, email, pin, birthday: birthday || undefined };
      // NEW: 記住電話
      localStorage.setItem('pass_phone', p);
      const { data } = await axios.post('/auth/pass/register', payload);
      setBusy(false);
      if (data?.ok) window.location.replace(getReturnUrl()); else setMsg('註冊失敗，請稍後再試');
    } catch (e2) {
      setBusy(false);
      if (e2?.response?.status === 409) setMsg('此電話或 Email 已註冊，請改用登入');
      else setMsg('註冊失敗，請稍後再試');
    }
  };

  // NEW: 一鍵切換電話（清除記錄並回到電話頁）
  const clearRememberedPhone = () => {
    localStorage.removeItem('pass_phone');
    setPhone('');
    setPin('');
    setPin2('');
    setMsg('');
    setStep('phone');
    setTimeout(() => phoneRef.current?.focus?.(), 50);
  };

  return (
    <FullBleed bg={bg}>
      <StageFrame
        designW={1080}
        designH={1920}
        maxPcWidth={440}
        overlay={({ px }) => (
          <>
            <style>{`
              .apply-tab { position:absolute; left:${px(TAB_POS.x)}px; top:${px(TAB_POS.y)}px; width:${px(TAB_POS.w)}px; height:auto; pointer-events:none; }
              .pl-wrap { position:absolute; left:${px(140)}px; right:${px(140)}px; top:${px(500)}px; color:#111; }
              .pl-h1 { font-family:"CreamFont","CSong3HK",system-ui,-apple-system,sans-serif; font-weight:800; font-size:${px(74)}px; line-height:1.12; margin:0 0 ${px(10)}px; }
              .pl-p  { font-family:"CSong3HK",system-ui,-apple-system,sans-serif; font-size:${px(40)}px; margin:0 0 ${px(18)}px; color:#222; }
              .pl-row{ display:grid; gap:${px(12)}px; margin:${px(14)}px 0 ${px(18)}px; }
              .pl-inp{ width:100%; box-sizing:border-box; font-family:"CSong3HK",system-ui,-apple-system,sans-serif; font-size:${px(48)}px; padding:${px(14)}px ${px(6)}px; background:transparent; color:#111; border:none; border-bottom:2px solid rgba(0,0,0,.85); outline:none; }
              .pl-inp::placeholder { color:rgba(0,0,0,.45); }
              .pl-btn{ width:100%; box-sizing:border-box; font-family:"CreamFont","CSong3HK",system-ui,-apple-system,sans-serif; font-size:${px(46)}px; font-weight:900; padding:${px(14)}px; border-radius:${px(10)}px; border:1px solid #111; background:transparent; color:#111; cursor:pointer; }
              .pl-btn:active{ transform:translateY(1px); }
              .pl-err{ display:inline-block; margin-top:${px(12)}px; background:#fff1f0; color:#b00020; padding:${px(8)}px ${px(10)}px; border-radius:${px(10)}px; font-weight:800; font-size:${px(28)}px; }
              .pl-note{ font-size:${px(32)}px; opacity:.6; margin-top:${px(8)}px; }
              .pl-actions{ display:grid; gap:${px(14)}px; }
              .pl-small{ font-size:${px(32)}px; opacity:.7; }
              .pl-link{ font-size:${px(32)}px; text-decoration:underline; cursor:pointer; opacity:.8; }
            `}</style>

            <img className="apply-tab" src={cardApply} alt="" />

            <div className="pl-wrap">
              {step === 'phone' && (
                <form onSubmit={onSubmitPhone}>
                  <h1 className="pl-h1">電話登入 / 註冊</h1>
                  <p className="pl-p">請輸入電話</p>
                  <div className="pl-row">
                    <PhoneInput
                      value={phone}
                      onChange={(val)=>setPhone(val)}
                      placeholder="例：912345678"
                      selectStyle={{
                        padding: `${px(14)}px ${px(6)}px`,
                        fontSize: `${px(40)}px`,
                        background: 'transparent',
                        color: '#111',
                        border: 'none',
                        borderBottom: '2px solid rgba(0,0,0,.85)',
                        borderRadius: 0,
                        minWidth: 80,
                        boxSizing: 'border-box',
                      }}
                      inputStyle={{
                        flex: 1,
                        minWidth: 0,
                        padding: `${px(14)}px ${px(6)}px`,
                        fontSize: `${px(48)}px`,
                        background: 'transparent',
                        color: '#111',
                        border: 'none',
                        borderBottom: '2px solid rgba(0,0,0,.85)',
                        borderRadius: 0,
                        fontWeight: 400,
                        boxSizing: 'border-box',
                      }}
                    />
                    <span className="pl-note">點國碼可搜尋國家；號碼開頭的 0 會自動去除</span>
                    {localStorage.getItem('pass_phone') && (
                      <span
                        className="pl-link"
                        onClick={() => {
                          setPhone(localStorage.getItem('pass_phone') || '');
                        }}
                      >
                        使用上一次的電話（{localStorage.getItem('pass_phone')}）
                      </span>
                    )}
                  </div>
                  <div className="pl-actions">
                    <button className="pl-btn" type="submit" disabled={busy}>
                      {busy ? '檢查中…' : '下一步'}
                    </button>
                    <button type="button" className="pl-btn" onClick={()=>window.location.assign('/')}>
                      返回首頁
                    </button>
                  </div>
                  {msg && <div className="pl-err">⚠️ {msg}</div>}
                </form>
              )}

              {step === 'pin' && (
                <form onSubmit={onSubmitPinLogin}>
                  <h1 className="pl-h1">輸入 PIN 碼</h1>
                  <p className="pl-p">
                    請輸入 PIN 碼登入
                    {'　'}
                    <span className="pl-link" onClick={clearRememberedPhone}>不是我本人？換電話</span>
                  </p>
                  <div className="pl-row">
                    <input className="pl-inp" value={phone} readOnly />
                    <input
                      ref={pinRef}
                      className="pl-inp"
                      placeholder="PIN（4~8 位數字）"
                      inputMode="numeric"
                      value={pin}
                      onChange={e=>setPin(e.target.value)}
                      required
                    />
                  </div>
                  <div className="pl-actions">
                    <button className="pl-btn" type="submit" disabled={busy}>
                      {busy ? '登入中…' : '登入'}
                    </button>
                    <button type="button" className="pl-btn" onClick={()=>setStep('phone')}>
                      返回上一步
                    </button>
                  </div>
                  {msg && <div className="pl-err">⚠️ {msg}</div>}
                </form>
              )}

              {step === 'setpin' && (
                <form onSubmit={onSubmitSetPin}>
                  <h1 className="pl-h1">設定 PIN</h1>
                  <p className="pl-p">
                    已有你的會員資料，請設定 PIN碼
                    {'　'}
                    <span className="pl-link" onClick={clearRememberedPhone}>不是我本人？換電話</span>
                  </p>
                  <div className="pl-row">
                    <input className="pl-inp" value={phone} readOnly />
                    <input className="pl-inp" value={name} readOnly />
                    <input className="pl-inp" value={email} readOnly />
                    <input
                      className="pl-inp"
                      type="date"
                      placeholder="請選擇生日"
                      value={birthday}
                      onChange={e=>setBirthday(e.target.value)}
                    />
                    <span className="pl-small">生日格式：YYYY-MM-DD（可留空）</span>
                    <input
                      ref={pinRef}
                      className="pl-inp"
                      placeholder="設定 PIN（4~8 位數字）"
                      inputMode="numeric"
                      value={pin}
                      onChange={e=>setPin(e.target.value)}
                      required
                    />
                    <input
                      className="pl-inp"
                      placeholder="再輸入一次 PIN"
                      inputMode="numeric"
                      value={pin2}
                      onChange={e=>setPin2(e.target.value)}
                      required
                    />
                  </div>
                  <div className="pl-actions">
                    <button className="pl-btn" type="submit" disabled={busy}>
                      {busy ? '設定中…' : '設定並登入'}
                    </button>
                    <button type="button" className="pl-btn" onClick={()=>setStep('phone')}>
                      返回上一步
                    </button>
                  </div>
                  {msg && <div className="pl-err">⚠️ {msg}</div>}
                </form>
              )}

              {step === 'register' && (
                <form onSubmit={onSubmitRegister}>
                  <h1 className="pl-h1">快速註冊</h1>
                  <p className="pl-p">
                    請補上資料完成註冊
                    {'　'}
                    <span className="pl-link" onClick={clearRememberedPhone}>不是我本人？換電話</span>
                  </p>
                  <div className="pl-row">
                    <input className="pl-inp" value={phone} readOnly />
                    <input
                      ref={emailRef}
                      className="pl-inp"
                      placeholder="Email"
                      inputMode="email"
                      value={email}
                      onChange={e=>setEmail(e.target.value)}
                      required
                    />
                    <input
                      className="pl-inp"
                      placeholder="姓名 / Name"
                      value={name}
                      onChange={e=>setName(e.target.value)}
                      required
                    />
                    <input
                      className="pl-inp"
                      type="date"
                      placeholder="生日（YYYY-MM-DD）"
                      value={birthday}
                      onChange={e=>setBirthday(e.target.value)}
                    />
                    <input
                      className="pl-inp"
                      placeholder="設定 PIN（4~8 位數字）"
                      inputMode="numeric"
                      value={pin}
                      onChange={e=>setPin(e.target.value)}
                      required
                    />
                    <input
                      className="pl-inp"
                      placeholder="再輸入一次 PIN"
                      inputMode="numeric"
                      value={pin2}
                      onChange={e=>setPin2(e.target.value)}
                      required
                    />
                  </div>
                  <div className="pl-actions">
                    <button className="pl-btn" type="submit" disabled={busy}>
                      {busy ? '註冊中…' : '註冊並登入'}
                    </button>
                    <button type="button" className="pl-btn" onClick={()=>setStep('phone')}>
                      返回上一步
                    </button>
                  </div>
                  {msg && <div className="pl-err">⚠️ {msg}</div>}
                </form>
              )}
            </div>
          </>
        )}
      />
    </FullBleed>
  );
}
